import ModalWrapper from '@app/common/ModalWrapper';
import SystemHint from '@app/common/SystemHint';
import {
  DraftEnvelopePrepositionedMarkType,
  EnvelopeAllovedActions,
  Instance,
  Modal,
} from '@app/const/consts';
import { useAppDispatch, useAppSelector } from '@app/hooks/redux';
import { resetModal, setModal } from '@app/store/modules/modal';
import {
  ICurrentEnvelope,
  IEnvelopeDoSignItem,
  IEnvelope,
  IEnvelopeTasks,
  IEnveloperPrepositionedMark,
} from '@app/store/modules/envelopes/types';
import EnvelopesService from '@services/envelopes';
import TasksService, {
  CloudDigitalSigningProvider,
  CloudDigitalSigningResultStatus,
  ICloudDigitalSigningRequest,
  IGraphicalSigningRequest,
} from '@services/tasks';
import UsersService from '@services/users';
import { setProfileSignature, setUserSettings } from '@store/modules/user';
import {
  IOrganisationSignatureInlineRequest,
  IUserOrganisationSignature,
  IVnFptSigningParameters,
  PdfSignatureStampLayout,
} from '@store/modules/user/types';
import {
  Alert,
  Button,
  Checkbox,
  FormLabel,
  IconCheck,
  IconCloud,
  IconEdit,
  IconProhibited,
  IconWarning,
  ILibModalSizes,
  Input,
  ILibOption,
  LibFixedType,
  RadioButton,
  SelectWrapper,
  Spinner,
  Table,
  getCheckboxCheckedValue,
} from '@infologistics/frontend-libraries';
import React, { ChangeEvent, DragEvent, FC, ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { IEnvelopeMassActionModalProps as IProps } from '@views/envelopes/types';
import { displayBasicNotification, displayErrorNotification } from '@utils/utils';
import cn from 'classnames';
import { Buffer } from 'buffer';
import { DateTime } from 'luxon';
import styles from './EnvelopeMassActionModal.module.scss';
import {
  FPT_PASSCODE_INPUT_PROPS,
  FPT_PROVIDER_USER_IDENTITY_INPUT_PROPS,
} from '../../fptCredentials';
import {
  compressOrganisationStampBase64,
  getImageBase64Src,
  readCompressedOrganisationStampFile,
} from '../../organisationSignatureImage';
import {
  canReuseVnFptSigningParameters,
  getNextVnFptSigningSettings,
  getVnFptSigningSettingsKey,
  hasStoredVnFptPasscode,
  sanitizeVnFptSigningSettings,
} from './vnFptSigningSettings';
import {
  createProfileSignatureUpdate,
  getMassActionOperationKey,
  getMassActionRowKey,
  getMassSigningType,
  getNextSigningFlowStage,
  MassSigningType,
  SigningFlowStage,
  shouldUpdateProfileSignature,
} from './massActionExecution';
import CryptoProProvider from '../../EnvelopeSignModal/CryptoPro/CryptoProProvider';
import { ILocalCertificate } from '../../EnvelopeSignModal/types';
import {
  Certificate,
} from '../../EnvelopeSignModal/CertificateSettings/types';
import {
  getCertificateData,
  getCertificateValidity,
} from '../../EnvelopeSignModal/utils';
import { base64ToArrayBuffer } from '../../EnvelopeSignModal/SignAction/utils';
import CertificatePower from '../../EnvelopeSignModal/CertificatePower';
import CertificateSettings from '../../EnvelopeSignModal/CertificateSettings/CertificateSettings';
import GraphicalSignatureForm, { GraphicalSignatureSource } from './GraphicalSignatureForm';

enum MassActionStatus {
  QUEUED = 'QUEUED',
  PROCESSING = 'PROCESSING',
  DONE = 'DONE',
  SKIPPED = 'SKIPPED',
  WARNING = 'WARNING',
  ERROR = 'ERROR',
}

enum VnSigningStep {
  METHOD = 'METHOD',
  SIGNER_DATA = 'SIGNER_DATA',
  SIGN = 'SIGN',
}

type OrganisationDetailsTextField = 'companyName' | 'taxCode' | 'position';

interface IEnvelopeMassActionState {
  status: MassActionStatus;
  error?: string;
}

interface ISigningParamsSnapshot {
  isAssignedToBoxUser: boolean;
  isSignOnBehalfOfOrganization: boolean | null;
  isAllowElectronicSignature: boolean;
  signerType: string;
}

interface ISignatureRetryState {
  key: string;
  message: string;
}

interface ISignatureValidationError extends Error {
  isSignatureValidationError: boolean;
  status?: CloudDigitalSigningResultStatus;
}

interface IRuSigningSelection {
  certificate: ILocalCertificate;
  powerOfAttorneyOguid: string | null;
}

type GraphicalMarkValue = boolean | string;
type GraphicalMarkValues = Record<string, Record<string, GraphicalMarkValue>>;

const EDITABLE_GRAPHICAL_MARK_TYPES = new Set<string>([
  DraftEnvelopePrepositionedMarkType.CHECKBOX,
  DraftEnvelopePrepositionedMarkType.TEXT,
  DraftEnvelopePrepositionedMarkType.SELECT,
  DraftEnvelopePrepositionedMarkType.STRING,
  DraftEnvelopePrepositionedMarkType.CUSTOM,
]);

const isEditableGraphicalMark = (mark: IEnveloperPrepositionedMark): boolean =>
  !mark.isReadOnly && EDITABLE_GRAPHICAL_MARK_TYPES.has(mark.type);

const getInitialGraphicalMarkValue = (
  mark: IEnveloperPrepositionedMark
): GraphicalMarkValue => {
  if (mark.type === DraftEnvelopePrepositionedMarkType.CHECKBOX) {
    const value = String(mark.value).toLowerCase();

    return value === 'true' || value === '1';
  }

  return mark.value ?? '';
};

const getInitialGraphicalMarkValues = (
  marks: IEnveloperPrepositionedMark[]
): Record<string, GraphicalMarkValue> => {
  const editableMarks = marks.filter(isEditableGraphicalMark);
  const values = editableMarks.reduce<Record<string, GraphicalMarkValue>>(
    (result, mark) => ({
      ...result,
      [mark.key]: getInitialGraphicalMarkValue(mark),
    }),
    {}
  );
  const customLabels = new Set(
    editableMarks
      .filter(({ label, type }) =>
        type === DraftEnvelopePrepositionedMarkType.CUSTOM && Boolean(label)
      )
      .map(({ label }) => label as string)
  );

  customLabels.forEach((label) => {
    const matchingMarks = editableMarks.filter((mark) =>
      mark.type === DraftEnvelopePrepositionedMarkType.CUSTOM && mark.label === label
    );
    const sharedValue = matchingMarks
      .map(({ key }) => values[key])
      .find((value) => typeof value === 'string' && Boolean(value.trim())) ?? '';

    matchingMarks.forEach(({ key }) => {
      values[key] = sharedValue;
    });
  });

  return values;
};

const DECLINE_ACTIONS = [
  EnvelopeAllovedActions.DECLINE_APPROVE,
  EnvelopeAllovedActions.DECLINE_SIGN,
];

const getActionTranslation = (action: EnvelopeAllovedActions | null): string => {
  switch (action) {
    case EnvelopeAllovedActions.SIGN:
      return 'envelopes:actions.sign';
    case EnvelopeAllovedActions.APPROVE:
      return 'envelopes:actions.approve';
    case EnvelopeAllovedActions.DECLINE_SIGN:
      return 'envelopes:actions.declineSign';
    case EnvelopeAllovedActions.DECLINE_APPROVE:
      return 'envelopes:actions.declineApprove';
    case EnvelopeAllovedActions.STOP_PROCESS:
      return 'envelopes:actions.stopProcess';
    default:
      return 'common:actions';
  }
};

const getDialogActionTranslation = (action: EnvelopeAllovedActions | null): string => {
  if (action && DECLINE_ACTIONS.includes(action)) return 'common:decline';

  return getActionTranslation(action);
};

const getTaskFromEnvelope = (envelope: ICurrentEnvelope): IEnvelopeTasks | undefined =>
  envelope.flow
    .flatMap(({ tasks }) => tasks)
    .find(({ oguid }) => oguid === envelope.currentUserTaskOguid);

const getSigningParamsSnapshot = (envelope: ICurrentEnvelope): ISigningParamsSnapshot | null => {
  const recipient = getTaskFromEnvelope(envelope)?.recipient;

  if (!recipient) return null;

  return {
    isAssignedToBoxUser: Boolean(recipient.isAssignedToBoxUser),
    isSignOnBehalfOfOrganization: recipient.signingParams?.isSignOnBehalfOfOrganization ??
      recipient.isSignOnBehalfOfOrganization ??
      null,
    isAllowElectronicSignature: recipient.signingParams.isAllowElectronicSignature,
    signerType: recipient.signingParams.signerType,
  };
};

const isSameSigningParams = (
  firstParams: ISigningParamsSnapshot,
  nextParams: ISigningParamsSnapshot
): boolean =>
  firstParams.isSignOnBehalfOfOrganization === nextParams.isSignOnBehalfOfOrganization &&
  firstParams.isAllowElectronicSignature === nextParams.isAllowElectronicSignature;

const createSignatureValidationError = (
  message: string,
  status: CloudDigitalSigningResultStatus
): ISignatureValidationError =>
  Object.assign(new Error(message), { isSignatureValidationError: true, status });

const isSignatureValidationError = (err: any): boolean =>
  Boolean(err?.isSignatureValidationError);

const isCertificateDoNotMatchError = (err: any): boolean =>
  isSignatureValidationError(err) &&
  (
    err.status === CloudDigitalSigningResultStatus.CERIFICATE_DO_NOT_MATCH ||
    err.status === CloudDigitalSigningResultStatus.CERTIFICATE_DO_NOT_MATCH
  );

const getErrorMessage = (err: any, fallback: string): string => {
  const message = err?.response?.data?.message ?? err?.message ?? err;

  return typeof message === 'string' && message ? message : fallback;
};

export const EnvelopeMassActionModal: FC<IProps> = ({
  action,
  skipReview = false,
  selectedEnvelopes,
  onCloseModal,
  onTasksUpdated,
}) => {
  const { t } = useTranslation([
    'common',
    'envelopes',
    'notification',
    'envelope',
    'profile',
    'crypto',
  ]);
  const dispatch = useAppDispatch();
  const instance = useAppSelector((state) => state.utils.instance);
  const { profile, settings: userSettings } = useAppSelector((state) => state.user);
  const mandates = useAppSelector((state) => state.user.mandates);
  const [modalEnvelopes] = useState(() => selectedEnvelopes);

  const [rows, setRows] = useState<Record<string, IEnvelopeMassActionState>>({});
  const [comment, setComment] = useState('');
  const [processing, setProcessing] = useState(false);
  const [processed, setProcessed] = useState(false);
  const [globalError, setGlobalError] = useState('');
  const [signingFlowStage, setSigningFlowStage] = useState(SigningFlowStage.REVIEW);
  const [selectedRuCertificate, setSelectedRuCertificate] = useState<ILocalCertificate | null>(null);
  const [isRuCertificateSettingsOpen, setIsRuCertificateSettingsOpen] = useState(false);
  const [isRuPluginAvailable, setIsRuPluginAvailable] = useState(true);
  const [organisationSignatures, setOrganisationSignatures] = useState<IUserOrganisationSignature[]>([]);
  const [organisationSignatureOguid, setOrganisationSignatureOguid] = useState<string | null>(null);
  const [isOrganisationSignaturesLoading, setIsOrganisationSignaturesLoading] = useState(false);
  const [organisationDetails, setOrganisationDetails] = useState<IOrganisationSignatureInlineRequest>({
    companyName: '',
    taxCode: '',
    fullName: null,
    position: '',
    stampBase64: '',
    layout: PdfSignatureStampLayout.IMAGE_TOP,
  });
  const [isOrganisationEditMode, setIsOrganisationEditMode] = useState(true);
  const [signerFullName, setSignerFullName] = useState('');
  const [isSignerNameOmitted, setIsSignerNameOmitted] = useState(false);
  const [providerUserIdentity, setProviderUserIdentity] = useState('');
  const [passcode, setPasscode] = useState('');
  const [checkingSignData, setCheckingSignData] = useState(
    action === EnvelopeAllovedActions.SIGN &&
    modalEnvelopes.some((envelope) => envelope.allowedMassActions?.includes(EnvelopeAllovedActions.SIGN))
  );
  const [availableSigningTypes, setAvailableSigningTypes] = useState<MassSigningType[]>([]);
  const [selectedSigningType, setSelectedSigningType] = useState<MassSigningType | null>(null);
  const [signingTypeByEnvelope, setSigningTypeByEnvelope] = useState<Record<string, MassSigningType>>({});
  const [signingPreflightErrors, setSigningPreflightErrors] = useState<Record<string, string>>({});
  const [graphicalSignerFullName, setGraphicalSignerFullName] = useState(profile.fullName ?? '');
  const [graphicalSignatureFull, setGraphicalSignatureFull] = useState<string | null>(
    profile.electronicSignatureImage
  );
  const [graphicalSignature, setGraphicalSignature] = useState<string | null>(
    profile.electronicSignatureImage
  );
  const [graphicalSignatureSource, setGraphicalSignatureSource] =
    useState<GraphicalSignatureSource>(
      profile.electronicSignatureImage
        ? GraphicalSignatureSource.PROFILE
        : GraphicalSignatureSource.DRAW
    );
  const [graphicalMarkValues, setGraphicalMarkValues] = useState<GraphicalMarkValues>({});
  const [requiresReusableSigner, setRequiresReusableSigner] = useState(false);
  const [envelopeDetails, setEnvelopeDetails] = useState<Record<string, ICurrentEnvelope>>({});
  const [firstSigningParams, setFirstSigningParams] = useState<ISigningParamsSnapshot | null>(null);
  const [signDataError, setSignDataError] = useState('');
  const [vnSigningStep, setVnSigningStep] = useState(VnSigningStep.METHOD);
  const [signatureRetry, setSignatureRetry] = useState<ISignatureRetryState | null>(null);
  const [isCredentialsOpen, setIsCredentialsOpen] = useState(false);
  const [isUsingSavedFptParameters, setIsUsingSavedFptParameters] = useState(false);
  const [isSavedFptParametersResolved, setIsSavedFptParametersResolved] = useState(false);
  const [isOrganisationPickerOpen, setIsOrganisationPickerOpen] = useState(false);
  const organisationPickerRef = useRef<HTMLDivElement | null>(null);
  const singleActionStartedRef = useRef(false);
  const ruCryptoRef = useRef(new CryptoProProvider(setGlobalError));
  const profileSignatureRef = useRef(profile.electronicSignatureImage);
  const hasPersistedFptParametersRef = useRef(false);
  const initialUserSettingsRef = useRef(userSettings);
  const initialVnFptSigningSettingsRef = useRef(
    sanitizeVnFptSigningSettings(userSettings.vnFptSigningSettings)
  );
  const hasInitialVnFptSigningSettings = Boolean(
    initialVnFptSigningSettingsRef.current?.personal ||
    initialVnFptSigningSettingsRef.current?.organisation
  );

  const isDeclineAction = action ? DECLINE_ACTIONS.includes(action) : false;
  const isMassSignAction = action === EnvelopeAllovedActions.SIGN;
  const isElectronicSigningSelected =
    isMassSignAction && selectedSigningType === MassSigningType.ELECTRONIC;
  const isGraphicalSigningSelected =
    isMassSignAction && selectedSigningType === MassSigningType.GRAPHICAL;
  const isFptSignAction =
    instance === Instance.VN &&
    isElectronicSigningSelected &&
    signingFlowStage === SigningFlowStage.VN_FPT;
  const isGraphicalSignFlow =
    isGraphicalSigningSelected &&
    (
      signingFlowStage === SigningFlowStage.GRAPHICAL_DRAW ||
      signingFlowStage === SigningFlowStage.GRAPHICAL_MARKS ||
      signingFlowStage === SigningFlowStage.GRAPHICAL_RESULTS
    );
  const isRuElectronicSignAction =
    instance === Instance.RU && isElectronicSigningSelected;
  const isSigningDetailsLoading = isMassSignAction && checkingSignData;
  const usesMassActionModalLayout =
    signingFlowStage === SigningFlowStage.REVIEW || isFptSignAction || isGraphicalSignFlow;
  const currentMassBoxOguid = modalEnvelopes[0]?.box.oguid ?? profile.boxOguid;
  const ruMandates = mandates.filter(({ boxOguid }) => boxOguid === currentMassBoxOguid);
  const ruCertificates = (userSettings.selectedCertificates ?? [])
    .map(({ certificate }) => certificate)
    .filter((certificate): certificate is ILocalCertificate => 'validToDate' in certificate);

  useEffect(() => {
    profileSignatureRef.current = profile.electronicSignatureImage;
  }, [profile.electronicSignatureImage]);

  useEffect(() => {
    const initialUserSettings = initialUserSettingsRef.current;
    const initialFptSigningSettings = initialUserSettings.vnFptSigningSettings;

    if (!isFptSignAction || !hasStoredVnFptPasscode(initialFptSigningSettings)) return;

    const sanitizedFptSigningSettings =
      sanitizeVnFptSigningSettings(initialFptSigningSettings);

    dispatch(setUserSettings({
      vnFptSigningSettings: sanitizedFptSigningSettings,
    }));
    UsersService.updateUserSettings({
      ...initialUserSettings,
      vnFptSigningSettings: sanitizedFptSigningSettings,
    }, true).catch(displayErrorNotification);
  }, [dispatch, isFptSignAction]);

  useEffect(() => {
    if (!usesMassActionModalLayout) return undefined;

    const modalElement = window.document.querySelector('.fl-modal_content-wrapper');

    modalElement?.classList.add('envelope-mass-action-modal');

    return () => {
      modalElement?.classList.remove('envelope-mass-action-modal');
    };
  }, [usesMassActionModalLayout]);

  useEffect(() => {
    if (!isOrganisationPickerOpen) return undefined;

    const handleDocumentPointer = (evt: Event): void => {
      const target = evt.target;

      if (target instanceof Node && organisationPickerRef.current?.contains(target)) return;

      setIsOrganisationPickerOpen(false);
    };

    document.addEventListener('pointerdown', handleDocumentPointer, true);
    document.addEventListener('click', handleDocumentPointer, true);

    return () => {
      document.removeEventListener('pointerdown', handleDocumentPointer, true);
      document.removeEventListener('click', handleDocumentPointer, true);
    };
  }, [isOrganisationPickerOpen]);

  const isEnvelopeMassActionAllowed = useCallback((envelope: IEnvelope): boolean =>
    Boolean(
      action &&
      (action === EnvelopeAllovedActions.STOP_PROCESS || envelope.currentUserTaskOguid) &&
      envelope.allowedMassActions?.includes(action)
    ), [action]);

  const isEnvelopeActionAllowed = useCallback((envelope: IEnvelope): boolean => {
    if (!isEnvelopeMassActionAllowed(envelope)) return false;
    const key = getMassActionRowKey(envelope);

    if (action === EnvelopeAllovedActions.SIGN && signingPreflightErrors[key]) return false;
    if (action !== EnvelopeAllovedActions.SIGN || !selectedSigningType) return true;

    return signingTypeByEnvelope[key] === selectedSigningType;
  }, [
    action,
    isEnvelopeMassActionAllowed,
    selectedSigningType,
    signingPreflightErrors,
    signingTypeByEnvelope,
  ]);

  const rowKeys = useMemo(
    () => modalEnvelopes.map(getMassActionRowKey),
    [modalEnvelopes]
  );

  const allowedCount = useMemo(
    () => modalEnvelopes.filter(isEnvelopeActionAllowed).length,
    [isEnvelopeActionAllowed, modalEnvelopes]
  );

  const graphicalEnvelopeFields = useMemo(() => {
    if (!isGraphicalSigningSelected) return [];

    return modalEnvelopes.flatMap((envelope) => {
      if (!isEnvelopeActionAllowed(envelope)) return [];

      const envelopeKey = getMassActionRowKey(envelope);
      const details = envelopeDetails[envelopeKey];
      const recipient = details ? getTaskFromEnvelope(details)?.recipient : undefined;

      if (!recipient) return [];

      return [{
        envelope,
        envelopeKey,
        marks: recipient.prepositionedMarks ?? [],
        recipient,
      }];
    });
  }, [
    envelopeDetails,
    isEnvelopeActionAllowed,
    isGraphicalSigningSelected,
    modalEnvelopes,
  ]);

  const hasGraphicalSignatureMarks = useMemo(
    () => graphicalEnvelopeFields.some(({ marks }) =>
      marks.some(({ type }) => type === DraftEnvelopePrepositionedMarkType.SIGNATURE)
    ),
    [graphicalEnvelopeFields]
  );

  const hasEditableGraphicalMarks = useMemo(
    () => graphicalEnvelopeFields.some(({ marks }) => marks.some(isEditableGraphicalMark)),
    [graphicalEnvelopeFields]
  );

  const areRequiredGraphicalMarksFilled = useMemo(
    () => graphicalEnvelopeFields.every(({ envelopeKey, marks }) =>
      marks.filter(isEditableGraphicalMark).every((mark) => {
        if (!mark.isRequired || mark.type === DraftEnvelopePrepositionedMarkType.CHECKBOX) {
          return true;
        }

        const value = graphicalMarkValues[envelopeKey]?.[mark.key];

        return typeof value === 'string' && Boolean(value.trim());
      })
    ),
    [graphicalEnvelopeFields, graphicalMarkValues]
  );

  const completedCount = useMemo(
    () => Object.values(rows).filter(({ status }) => status === MassActionStatus.DONE).length,
    [rows]
  );

  const requiresOrganizationSigning = useMemo(
    () => Boolean(firstSigningParams?.isSignOnBehalfOfOrganization),
    [firstSigningParams]
  );
  const fptSigningSettingsKey = getVnFptSigningSettingsKey(requiresOrganizationSigning);
  const savedFptParameters =
    initialVnFptSigningSettingsRef.current?.[fptSigningSettingsKey];
  const isSavedFptParametersLoading =
    isFptSignAction &&
    hasInitialVnFptSigningSettings &&
    !signDataError &&
    (checkingSignData || !isSavedFptParametersResolved);

  const selectedOrganisationSignature = useMemo(
    () => organisationSignatures.find(({ oguid }) => oguid === organisationSignatureOguid) ?? null,
    [organisationSignatureOguid, organisationSignatures]
  );
  const isOrganisationDetailsIncomplete = requiresOrganizationSigning && (
    !organisationDetails.companyName ||
    !organisationDetails.taxCode ||
    !organisationDetails.stampBase64
  );
  const isSignerNameRequired = requiresReusableSigner;

  const getInitialRows = useCallback((): Record<string, IEnvelopeMassActionState> =>
    modalEnvelopes.reduce<Record<string, IEnvelopeMassActionState>>(
      (acc, envelope) => ({
        ...acc,
        [getMassActionRowKey(envelope)]: {
          status: isEnvelopeMassActionAllowed(envelope) ? MassActionStatus.QUEUED : MassActionStatus.SKIPPED,
        },
      }),
      {}
    ), [isEnvelopeMassActionAllowed, modalEnvelopes]);

  const resetSigningOperationState = useCallback((): void => {
    setRows(getInitialRows());
    setProcessed(false);
    setGlobalError('');
    setSignatureRetry(null);
    setIsCredentialsOpen(false);
  }, [getInitialRows]);

  useEffect(() => {
    resetSigningOperationState();
    setEnvelopeDetails({});
    setFirstSigningParams(null);
    setAvailableSigningTypes([]);
    setSelectedSigningType(null);
    setSigningTypeByEnvelope({});
    setSigningPreflightErrors({});
    setGraphicalSignerFullName(profile.fullName ?? '');
    setGraphicalSignatureFull(profileSignatureRef.current);
    setGraphicalSignature(profileSignatureRef.current);
    setGraphicalSignatureSource(
      profileSignatureRef.current
        ? GraphicalSignatureSource.PROFILE
        : GraphicalSignatureSource.DRAW
    );
    setGraphicalMarkValues({});
    setRequiresReusableSigner(false);
    setSignDataError('');
    setSigningFlowStage(SigningFlowStage.REVIEW);
    setSelectedRuCertificate(null);
    setIsRuCertificateSettingsOpen(false);
    setVnSigningStep(VnSigningStep.METHOD);
    setCheckingSignData(
      action === EnvelopeAllovedActions.SIGN &&
      modalEnvelopes.some((envelope) => envelope.allowedMassActions?.includes(EnvelopeAllovedActions.SIGN))
    );
    setSignerFullName('');
    setProviderUserIdentity('');
    setPasscode('');
    setIsUsingSavedFptParameters(false);
    setIsSavedFptParametersResolved(false);
    setIsOrganisationPickerOpen(false);
    setOrganisationSignatureOguid(null);
    setIsOrganisationSignaturesLoading(false);
    setOrganisationDetails({
      companyName: '',
      taxCode: '',
      fullName: null,
      position: '',
      stampBase64: '',
      layout: PdfSignatureStampLayout.IMAGE_TOP,
    });
    setIsOrganisationEditMode(true);
    setIsSignerNameOmitted(false);
    singleActionStartedRef.current = false;
    hasPersistedFptParametersRef.current = false;
  }, [
    action,
    instance,
    modalEnvelopes,
    profile.fullName,
    resetSigningOperationState,
  ]);

  useEffect(() => {
    if (!isFptSignAction || !requiresOrganizationSigning) return;

    setIsOrganisationSignaturesLoading(true);
    UsersService.getUserOrganisationSignatures()
      .then(({ data }) => {
        setOrganisationSignatures(data);
        const savedOrganisationOguid =
          initialVnFptSigningSettingsRef.current?.organisation?.organisationSignatureOguid;
        const defaultSignature = data.find(({ isDefault }) => isDefault) ?? data[0];
        const savedSignatureExists = data.some(({ oguid }) => oguid === savedOrganisationOguid);

        setOrganisationSignatureOguid(
          savedSignatureExists ? savedOrganisationOguid ?? null : defaultSignature?.oguid ?? null
        );
      })
      .catch(displayErrorNotification)
      .finally(() => setIsOrganisationSignaturesLoading(false));
  }, [isFptSignAction, requiresOrganizationSigning]);

  useEffect(() => {
    if (!requiresOrganizationSigning) setIsSignerNameOmitted(false);
  }, [requiresOrganizationSigning]);

  useEffect(() => {
    if (!isFptSignAction || !firstSigningParams) return;

    if (
      firstSigningParams.isAllowElectronicSignature === false &&
      canReuseVnFptSigningParameters(
        savedFptParameters,
        firstSigningParams.isAssignedToBoxUser,
        requiresOrganizationSigning
      )
    ) {
      const savedOrganisationDetails = savedFptParameters.organisationDetails;

      setSignerFullName(savedFptParameters.signerFullName);
      setIsSignerNameOmitted(
        requiresOrganizationSigning && savedFptParameters.isSignerNameOmitted
      );
      setProviderUserIdentity(savedFptParameters.providerUserIdentity);
      setOrganisationSignatureOguid(savedFptParameters.organisationSignatureOguid);
      if (savedOrganisationDetails) {
        setOrganisationDetails(savedOrganisationDetails);
        setIsOrganisationEditMode(false);
      }
      setVnSigningStep(VnSigningStep.SIGN);
      setIsUsingSavedFptParameters(true);
    }

    setIsSavedFptParametersResolved(true);
  }, [
    firstSigningParams,
    isFptSignAction,
    requiresOrganizationSigning,
    savedFptParameters,
  ]);

  useEffect(() => {
    if (!selectedOrganisationSignature) {
      if (!isUsingSavedFptParameters) setIsOrganisationEditMode(true);
      return;
    }

    if (
      isUsingSavedFptParameters &&
      selectedOrganisationSignature.oguid === savedFptParameters?.organisationSignatureOguid
    ) {
      return;
    }

    let isCancelled = false;

    const {
      companyName,
      taxCode,
      fullName,
      position,
      stampBase64,
      layout,
    } = selectedOrganisationSignature;

    compressOrganisationStampBase64(stampBase64)
      .then((compressedStampBase64) => {
        if (isCancelled) return;

        const details = {
          companyName,
          taxCode,
          fullName,
          position: position ?? '',
          stampBase64: compressedStampBase64,
          layout,
        };

        setOrganisationDetails(details);
        setIsOrganisationEditMode(
          !details.companyName ||
          !details.taxCode ||
          !details.stampBase64
        );
      });

    return () => {
      isCancelled = true;
    };
  }, [isUsingSavedFptParameters, savedFptParameters, selectedOrganisationSignature]);

  useEffect(() => {
    let isCancelled = false;

    if (!isMassSignAction) return;

    const allowedEnvelopes = modalEnvelopes.filter(isEnvelopeMassActionAllowed);

    if (!allowedEnvelopes.length) {
      setCheckingSignData(false);
      return;
    }

    setCheckingSignData(true);

    Promise
      .allSettled(
        allowedEnvelopes.map((envelope) =>
          EnvelopesService.getEnvelope({
            oguid: envelope.oguid,
            boxOguid: envelope.box.oguid,
            direction: envelope.direction,
          })
        )
      )
      .then((results) => {
        if (isCancelled) return;

        const preflightErrors = results.reduce<Record<string, string>>((acc, result, index) => {
          const envelope = allowedEnvelopes[index];
          const key = getMassActionRowKey(envelope);

          if (result.status === 'fulfilled') return acc;

          return {
            ...acc,
            [key]: getErrorMessage(result.reason, t('common:error')),
          };
        }, {});
        const details = results.reduce<Record<string, ICurrentEnvelope>>((acc, result, index) => {
          if (result.status !== 'fulfilled') return acc;

          const envelope = allowedEnvelopes[index];

          return {
            ...acc,
            [getMassActionRowKey(envelope)]: result.value.data,
          };
        }, {});

        setEnvelopeDetails(details);

        const resolvedTasks = allowedEnvelopes.flatMap((envelope) => {
          const key = getMassActionRowKey(envelope);
          if (preflightErrors[key]) return [];

          const envelopeDetails = details[key];
          const params = envelopeDetails ? getSigningParamsSnapshot(envelopeDetails) : null;
          const recipient = envelopeDetails ? getTaskFromEnvelope(envelopeDetails)?.recipient : undefined;

          if (!params || !recipient) {
            preflightErrors[key] = t('notification:taskNotFound');
            return [];
          }

          return [{ key, params, recipient }];
        });

        setSigningPreflightErrors(preflightErrors);

        if (!resolvedTasks.length) {
          setSigningTypeByEnvelope({});
          setAvailableSigningTypes([]);
          setSelectedSigningType(null);
          setRequiresReusableSigner(false);
          setFirstSigningParams(null);
          setSignDataError('');
          return;
        }

        const typeByEnvelope = resolvedTasks.reduce<Record<string, MassSigningType>>(
          (result, { key, params }) => ({
            ...result,
            [key]: getMassSigningType(params.isAllowElectronicSignature),
          }),
          {}
        );
        const signingTypes = Array.from(new Set(Object.values(typeByEnvelope)));
        const cloudTasks = resolvedTasks.filter(({ params }) =>
          !params.isAllowElectronicSignature
        );
        const initialSigningType = signingTypes[0] ?? null;
        const initiallySelectedTasks = initialSigningType
          ? resolvedTasks.filter(({ key }) => typeByEnvelope[key] === initialSigningType)
          : resolvedTasks;
        const firstResolvedTask = initiallySelectedTasks[0];
        const firstParams = firstResolvedTask.params;
        const reusableSignerTask = initiallySelectedTasks.find(({ params, recipient }) =>
          !recipient.isAssignedToBoxUser && (
            params.isAllowElectronicSignature ||
            params.isSignOnBehalfOfOrganization === false
          )
        );
        const recipientFullName = reusableSignerTask?.recipient.declaredSigner?.fullName ?? (
          firstResolvedTask.recipient.isAssignedToBoxUser
            ? profile.fullName
            : firstResolvedTask.recipient.declaredSigner?.fullName
        );

        setSigningTypeByEnvelope(typeByEnvelope);
        setAvailableSigningTypes(signingTypes);
        setSelectedSigningType(initialSigningType);
        setRequiresReusableSigner(Boolean(reusableSignerTask));
        setFirstSigningParams(firstParams);
        setSignerFullName(recipientFullName ?? '');
        setIsSignerNameOmitted(
          !reusableSignerTask &&
          Boolean(firstParams.isSignOnBehalfOfOrganization) &&
          recipientFullName === null
        );

        const firstCloudParams = cloudTasks[0]?.params;
        const isParamsMismatch = Boolean(
          firstCloudParams &&
          cloudTasks.some(({ params }) => !isSameSigningParams(firstCloudParams, params))
        );

        setSignDataError(
          instance === Instance.VN &&
          initialSigningType === MassSigningType.ELECTRONIC &&
          isParamsMismatch
            ? t('envelopes:massActions.signingParametersMismatch')
            : ''
        );
        setVnSigningStep(VnSigningStep.METHOD);
        if (
          instance === Instance.VN &&
          signingTypes.length === 1 &&
          initialSigningType === MassSigningType.ELECTRONIC &&
          allowedEnvelopes.length === modalEnvelopes.length &&
          Object.keys(preflightErrors).length === 0
        ) {
          setSigningFlowStage(SigningFlowStage.VN_FPT);
        }
      })
      .finally(() => {
        if (!isCancelled) setCheckingSignData(false);
      });

    return () => {
      isCancelled = true;
    };
  }, [instance, isEnvelopeMassActionAllowed, isMassSignAction, modalEnvelopes, profile.fullName, t]);

  const handleSigningTypeSelect = (signingType: MassSigningType): void => {
    const matchingTasks = modalEnvelopes.flatMap((envelope) => {
      const key = getMassActionRowKey(envelope);
      const details = envelopeDetails[key];
      const params = details ? getSigningParamsSnapshot(details) : null;
      const recipient = details ? getTaskFromEnvelope(details)?.recipient : undefined;

      return params && recipient && signingTypeByEnvelope[key] === signingType
        ? [{ params, recipient }]
        : [];
    });
    const firstTask = matchingTasks[0];

    if (!firstTask) return;

    const reusableSignerTask = matchingTasks.find(({ params, recipient }) =>
      !recipient.isAssignedToBoxUser && (
        params.isAllowElectronicSignature ||
        params.isSignOnBehalfOfOrganization === false
      )
    );
    const recipientFullName = reusableSignerTask?.recipient.declaredSigner?.fullName ?? (
      firstTask.recipient.isAssignedToBoxUser
        ? profile.fullName
        : firstTask.recipient.declaredSigner?.fullName
    );
    const isParamsMismatch = signingType === MassSigningType.ELECTRONIC &&
      matchingTasks.some(({ params }) => !isSameSigningParams(firstTask.params, params));

    resetSigningOperationState();
    setSelectedSigningType(signingType);
    setGraphicalSignerFullName(profile.fullName ?? '');
    setGraphicalSignatureFull(profile.electronicSignatureImage);
    setGraphicalSignature(profile.electronicSignatureImage);
    setGraphicalSignatureSource(
      profile.electronicSignatureImage
        ? GraphicalSignatureSource.PROFILE
        : GraphicalSignatureSource.DRAW
    );
    setGraphicalMarkValues({});
    setFirstSigningParams(firstTask.params);
    setRequiresReusableSigner(Boolean(reusableSignerTask));
    setSignerFullName(recipientFullName ?? '');
    setIsSignerNameOmitted(
      !reusableSignerTask &&
      Boolean(firstTask.params.isSignOnBehalfOfOrganization) &&
      recipientFullName === null
    );
    setSignDataError(
      instance === Instance.VN && isParamsMismatch
        ? t('envelopes:massActions.signingParametersMismatch')
        : ''
    );
    setVnSigningStep(VnSigningStep.METHOD);
  };

  const setRowState = (key: string, state: IEnvelopeMassActionState): void => {
    setRows((prev) => ({
      ...prev,
      [key]: state,
    }));
  };

  const handleCommentChange = (evt: ChangeEvent<HTMLInputElement>): void => {
    setComment(evt.target.value);
  };

  const handleOrganisationDetailsChange =
    (name: OrganisationDetailsTextField) =>
    (evt: ChangeEvent<HTMLInputElement>) => {
      setOrganisationDetails((details) => ({
        ...details,
        [name]: evt.target.value,
      }));
    };

  const handleSignerNameOmittedChange = (evt: ChangeEvent<HTMLInputElement>): void => {
    if (!requiresOrganizationSigning || requiresReusableSigner) return;

    const isChecked = evt.target.checked;

    setIsSignerNameOmitted(isChecked);
    if (isChecked) setSignerFullName('');
  };

  const setOrganisationStampFile = (file?: File): void => {
    if (!file) return;

    readCompressedOrganisationStampFile(file)
      .then((stampBase64) => {
        setOrganisationDetails((details) => ({
          ...details,
          stampBase64,
        }));
      })
      .catch(displayErrorNotification);
  };

  const handleOrganisationStampChange = (evt: ChangeEvent<HTMLInputElement>): void => {
    setOrganisationStampFile(evt.target.files?.[0]);
  };

  const handleOrganisationStampDragOver = (evt: DragEvent<HTMLLabelElement>): void => {
    evt.preventDefault();
  };

  const handleOrganisationStampDrop = (evt: DragEvent<HTMLLabelElement>): void => {
    evt.preventDefault();
    setOrganisationStampFile(evt.dataTransfer.files?.[0]);
  };

  const handleOrganisationStampRemoveBackground = (): void => {
    if (!organisationDetails.stampBase64) return;

    const image = new Image();

    image.onload = () => {
      const canvas = document.createElement('canvas');
      const context = canvas.getContext('2d');

      if (!context) return;

      canvas.width = image.naturalWidth;
      canvas.height = image.naturalHeight;
      context.drawImage(image, 0, 0);

      const imageData = context.getImageData(0, 0, canvas.width, canvas.height);
      const { data } = imageData;

      for (let index = 0; index < data.length; index += 4) {
        const red = data[index];
        const green = data[index + 1];
        const blue = data[index + 2];
        const brightness = (red + green + blue) / 3;

        if (red > 235 && green > 235 && blue > 235) {
          data[index + 3] = 0;
        } else if (red > 215 && green > 215 && blue > 215) {
          data[index + 3] = Math.round(data[index + 3] * ((255 - brightness) / 40));
        }
      }

      context.putImageData(imageData, 0, 0);

      compressOrganisationStampBase64(canvas.toDataURL('image/png'))
        .then((stampBase64) => {
          setOrganisationDetails((details) => ({
            ...details,
            stampBase64,
          }));
        })
        .catch(displayErrorNotification);
    };

    image.src = getImageBase64Src(organisationDetails.stampBase64);
  };

  const handleOrganisationEdit = (): void => {
    setIsOrganisationEditMode(true);
  };

  const handleOrganisationPickerToggle = (): void => {
    setIsOrganisationPickerOpen((isOpen) => !isOpen);
  };

  const handleOrganisationPick = ({ oguid }: IUserOrganisationSignature): void => {
    setOrganisationSignatureOguid(oguid);
    setIsOrganisationPickerOpen(false);
  };

  const getCloudSigningData = (envelope: ICurrentEnvelope): ICloudDigitalSigningRequest | null => {
    const currentTask = getTaskFromEnvelope(envelope);
    const currentRecipient = currentTask?.recipient;

    if (!currentRecipient) return null;

    const {
      companyName,
      taxCode,
      position,
      stampBase64,
      layout,
    } = organisationDetails;
    const userOrganisationSignatureOguid = requiresOrganizationSigning ? organisationSignatureOguid : null;

    const isAssignedToBoxUser = Boolean(currentRecipient.isAssignedToBoxUser);

    return {
      provider: CloudDigitalSigningProvider.FPT,
      signer: isAssignedToBoxUser || (requiresOrganizationSigning && isSignerNameOmitted)
        ? null
        : {
            fullName: signerFullName || currentRecipient.declaredSigner?.fullName || '',
            initials: currentRecipient.declaredSigner?.initials ?? null,
            position: currentRecipient.declaredSigner?.position ?? null,
          },
      passcode: passcode || null,
      providerUserIdentity: providerUserIdentity || null,
      userOrganisationSignatureOguid,
      organisationSignature: requiresOrganizationSigning
        ? {
            companyName,
            taxCode,
            position: position || null,
            stampBase64,
            layout,
          }
        : null,
    };
  };

  const getGraphicalSigningData = (
    envelope: IEnvelope,
    details: ICurrentEnvelope
  ): IGraphicalSigningRequest | null => {
    const currentRecipient = getTaskFromEnvelope(details)?.recipient;

    if (!currentRecipient) return null;
    if (!currentRecipient.isAssignedToBoxUser && !graphicalSignerFullName.trim()) return null;

    const envelopeKey = getMassActionRowKey(envelope);
    const recipientMarks = currentRecipient.prepositionedMarks ?? [];
    const hasSignatureMark = recipientMarks.some(
      ({ type }) => type === DraftEnvelopePrepositionedMarkType.SIGNATURE
    );

    if (hasSignatureMark && !graphicalSignature) return null;

    const marks = recipientMarks.reduce<Record<string, GraphicalMarkValue>>((result, mark) => {
      if (!isEditableGraphicalMark(mark)) return result;

      const value = graphicalMarkValues[envelopeKey]?.[mark.key] ??
        getInitialGraphicalMarkValue(mark);

      if (mark.type === DraftEnvelopePrepositionedMarkType.CHECKBOX) {
        if (mark.isRequired || value === true) result[mark.key] = Boolean(value);

        return result;
      }

      if (typeof value === 'string' && value.trim()) result[mark.key] = value;

      return result;
    }, {});

    const request: IGraphicalSigningRequest = {};

    if (!currentRecipient.isAssignedToBoxUser) {
      request.signer = {
        fullName: graphicalSignerFullName.trim(),
        initials: currentRecipient.declaredSigner?.initials ?? null,
        position: currentRecipient.declaredSigner?.position ?? null,
      };
    }

    if (hasSignatureMark) request.electronicSignatureBase64 = graphicalSignature;
    if (Object.keys(marks).length) request.marks = marks;

    return request;
  };

  const processRuElectronicSigning = async (
    envelope: IEnvelope,
    details: ICurrentEnvelope,
    selection: IRuSigningSelection
  ): Promise<void> => {
    const { certificate, powerOfAttorneyOguid } = selection;
    const mandate = powerOfAttorneyOguid
      ? ruMandates.find(({ oguid }) => oguid === powerOfAttorneyOguid)
      : null;

    if (powerOfAttorneyOguid && !mandate) {
      throw new Error(t('profile:PoA.expired'));
    }

    if (mandate) {
      const now = DateTime.now();
      const validFrom = DateTime.fromISO(mandate.validFrom);
      const validTo = mandate.validTo
        ? DateTime.fromISO(mandate.validTo).plus({ day: 1 })
        : null;

      if (now < validFrom || (validTo && now >= validTo)) {
        throw new Error(t('profile:PoA.expired'));
      }
    }

    const certificateBase64 = await ruCryptoRef.current.getCertificateInBase64(
      certificate.serialNumber
    );
    const base64Certificate = [
      '-----BEGIN CERTIFICATE-----',
      certificateBase64,
      '-----END CERTIFICATE-----',
    ].join('\n');
    const signatures: IEnvelopeDoSignItem[] = [];

    for (const file of details.files ?? []) {
      const hashResponse = await EnvelopesService.postEnvelopeDoHash(
        envelope.oguid,
        String(file.index),
        {
          base64Certificate,
          boxOguid: envelope.box.oguid,
          mchdOguid: powerOfAttorneyOguid,
        }
      );
      const signedHash = await ruCryptoRef.current.signHash(
        certificate,
        hashResponse.data,
        profile.useTimeService
      );

      if (typeof signedHash !== 'string') {
        throw new Error(
          signedHash.errMessage || signedHash.message || t('common:error')
        );
      }

      const signatureBytes = base64ToArrayBuffer(signedHash.replace(/\r?\n|\r/g, ''));
      const signatureHex = Buffer.from(signatureBytes).toString('hex');

      signatures.push({
        hash: hashResponse.data,
        signatureBase64: btoa(signatureHex),
      });
    }

    if (!signatures.length) throw new Error(t('notification:fileNotFound'));

    await EnvelopesService.postEnvelopeDoSign(envelope.oguid, { signatures });
  };

  const processEnvelope = async (
    envelope: IEnvelope,
    isIgnoreCertificateValidation = false,
    ruSigningSelection?: IRuSigningSelection
  ): Promise<void> => {
    if (!action) return;

    if (action === EnvelopeAllovedActions.STOP_PROCESS) {
      await EnvelopesService.postEnvelopeFlowStop(envelope.oguid);
      return;
    }

    if (!envelope.currentUserTaskOguid) return;

    if (DECLINE_ACTIONS.includes(action)) {
      await TasksService.postTaskDecline(envelope.currentUserTaskOguid, comment);
      return;
    }

    if (action === EnvelopeAllovedActions.SIGN) {
      const key = getMassActionRowKey(envelope);
      const data = envelopeDetails[key] ?? (await EnvelopesService.getEnvelope({
        oguid: envelope.oguid,
        boxOguid: envelope.box.oguid,
        direction: envelope.direction,
      })).data;
      const signingParams = getSigningParamsSnapshot(data);

      if (!signingParams) {
        throw new Error(t('notification:taskNotFound'));
      }

      if (signingParams.isAllowElectronicSignature) {
        const signingData = getGraphicalSigningData(envelope, data);

        if (!signingData) throw new Error(t('notification:taskNotFound'));

        await TasksService.postGraphicalSigning(
          envelope.currentUserTaskOguid,
          signingData
        );
        return;
      }

      if (instance === Instance.RU) {
        if (!ruSigningSelection) throw new Error(t('common:error'));

        await processRuElectronicSigning(envelope, data, ruSigningSelection);
        return;
      }

      if (instance !== Instance.VN) throw new Error(t('common:error'));

      const signingData = getCloudSigningData(data);

      if (!signingData) throw new Error(t('notification:taskNotFound'));

      const response = await TasksService.postCloudDigitalSigning(
        envelope.currentUserTaskOguid,
        {
          ...signingData,
          isIgnoreCertificateValidation,
        }
      );

      if (response.data.status !== CloudDigitalSigningResultStatus.SIGNED) {
        throw createSignatureValidationError(
          response.data.failure?.message ?? t('envelopes:signAction.cloud.failed'),
          response.data.status
        );
      }

      return;
    }

    await TasksService.postTaskComplete(envelope.currentUserTaskOguid, comment);
  };

  const persistFptSigningParameters = async (): Promise<boolean> => {
    if (firstSigningParams?.isAllowElectronicSignature !== false) return false;

    const parameters: IVnFptSigningParameters = {
      signerFullName,
      isSignerNameOmitted: requiresOrganizationSigning && isSignerNameOmitted,
      providerUserIdentity,
      organisationSignatureOguid: requiresOrganizationSigning ? organisationSignatureOguid : null,
      organisationDetails: requiresOrganizationSigning ? organisationDetails : null,
    };
    const previousFptSigningSettings =
      sanitizeVnFptSigningSettings(userSettings.vnFptSigningSettings);
    const nextFptSigningSettings = getNextVnFptSigningSettings(
      previousFptSigningSettings,
      fptSigningSettingsKey,
      parameters
    );

    dispatch(setUserSettings({ vnFptSigningSettings: nextFptSigningSettings }));

    try {
      await UsersService.updateUserSettings({
        ...userSettings,
        vnFptSigningSettings: nextFptSigningSettings,
      }, true);
      return true;
    } catch (err: any) {
      dispatch(setUserSettings({ vnFptSigningSettings: previousFptSigningSettings }));
      displayErrorNotification(err);
      return false;
    }
  };

  const handleSubmit = async (
    ruSigningSelection?: IRuSigningSelection
  ): Promise<void> => {
    if (!action || signDataError || checkingSignData) return;

    setProcessing(true);
    setGlobalError('');
    const retryKey = signatureRetry?.key;
    let hasCompletedTasks = false;
    const operationResults = new Map<string, IEnvelopeMassActionState>();

    for (const envelope of modalEnvelopes) {
      const key = getMassActionRowKey(envelope);
      const row = rows[key];
      const operationKey = getMassActionOperationKey(action, envelope);

      if (!isEnvelopeActionAllowed(envelope) || !operationKey) {
        setRowState(key, { status: MassActionStatus.SKIPPED });
        continue;
      }

      if (row?.status === MassActionStatus.DONE) continue;
      if (retryKey && key !== retryKey) continue;

      const existingResult = operationResults.get(operationKey);

      if (existingResult) {
        setRowState(key, existingResult);
        continue;
      }

      setRowState(key, { status: MassActionStatus.PROCESSING });

      try {
        await processEnvelope(envelope, retryKey === key, ruSigningSelection);
        hasCompletedTasks = true;
        const doneState = { status: MassActionStatus.DONE };

        operationResults.set(operationKey, doneState);
        setRowState(key, doneState);
        if (
          isFptSignAction &&
          !hasPersistedFptParametersRef.current
        ) {
          hasPersistedFptParametersRef.current = true;
          void persistFptSigningParameters()
            .then((isPersisted) => {
              hasPersistedFptParametersRef.current = isPersisted;
            });
        }
      } catch (err: any) {
        const message = getErrorMessage(err, t('common:error'));

        if (isFptSignAction && isCertificateDoNotMatchError(err) && retryKey !== key) {
          const warningState = {
            status: MassActionStatus.WARNING,
            error: message,
          };

          operationResults.set(operationKey, warningState);
          setRowState(key, warningState);
          setSignatureRetry({ key, message });
          if (hasCompletedTasks) onTasksUpdated();
          setProcessing(false);
          return;
        }

        if (retryKey === key) {
          setSignatureRetry(null);
        }

        const errorState = {
          status: MassActionStatus.ERROR,
          error: message,
        };

        operationResults.set(operationKey, errorState);
        setRowState(key, errorState);

        if (isFptSignAction) {
          setGlobalError(message);
        }
      }
    }

    setProcessed(true);
    if (retryKey) setSignatureRetry(null);
    if (hasCompletedTasks) onTasksUpdated();
    setProcessing(false);
  };

  const handleClose = (reload?: boolean): void => {
    dispatch(resetModal());
    onCloseModal(reload);
  };

  const handleCloseClick = () => handleClose();

  const handleSigningReviewSubmit = useCallback((): void => {
    if (!selectedSigningType) return;

    const nextStage = getNextSigningFlowStage(instance, selectedSigningType);

    if (nextStage === SigningFlowStage.GRAPHICAL_DRAW) {
      const initialMarkValues = graphicalEnvelopeFields.reduce<GraphicalMarkValues>(
        (result, { envelopeKey, marks }) => ({
          ...result,
          [envelopeKey]: getInitialGraphicalMarkValues(marks),
        }),
        {}
      );

      setGraphicalMarkValues((currentValues) =>
        Object.keys(currentValues).length ? currentValues : initialMarkValues
      );
      setSigningFlowStage(nextStage);
      return;
    }

    if (nextStage === SigningFlowStage.VN_FPT) {
      setVnSigningStep(VnSigningStep.METHOD);
      setSigningFlowStage(nextStage);
      return;
    }

    if (nextStage === SigningFlowStage.RU_CERTIFICATE) {
      setSigningFlowStage(nextStage);
      return;
    }

    setGlobalError(t('common:error'));
  }, [graphicalEnvelopeFields, instance, selectedSigningType, t]);

  useEffect(() => {
    if (
      !skipReview ||
      !isMassSignAction ||
      checkingSignData ||
      singleActionStartedRef.current
    ) return;

    const preflightError = Object.values(signingPreflightErrors)[0];
    const error = preflightError || signDataError;

    singleActionStartedRef.current = true;

    if (error || !selectedSigningType) {
      displayBasicNotification({
        content: error || t('common:error'),
        title: t('notification:errorTitle'),
        type: 'error',
      });
      dispatch(resetModal());
      onCloseModal(false);
      return;
    }

    if (signingFlowStage === SigningFlowStage.REVIEW) {
      handleSigningReviewSubmit();
    }
  }, [
    checkingSignData,
    dispatch,
    handleSigningReviewSubmit,
    isMassSignAction,
    onCloseModal,
    selectedSigningType,
    signDataError,
    signingFlowStage,
    signingPreflightErrors,
    skipReview,
    t,
  ]);

  useEffect(() => {
    if (
      !skipReview ||
      !isMassSignAction ||
      checkingSignData ||
      signingFlowStage === SigningFlowStage.REVIEW
    ) return;

    dispatch(setModal(Modal.ENVELOPE_MASS_ACTION));
  }, [checkingSignData, dispatch, isMassSignAction, signingFlowStage, skipReview]);

  const handleGraphicalSignatureChange = (
    fullSignature: string | null,
    trimmedSignature: string | null,
    source: GraphicalSignatureSource
  ): void => {
    setGraphicalSignatureFull(fullSignature);
    setGraphicalSignature(trimmedSignature);
    setGraphicalSignatureSource(source);
  };

  const persistGraphicalSignature = async (): Promise<boolean> => {
    if (!shouldUpdateProfileSignature(
      profile.electronicSignatureImage,
      graphicalSignature,
      graphicalSignatureSource === GraphicalSignatureSource.PROFILE
    )) return true;

    if (!graphicalSignature) return true;

    setProcessing(true);

    try {
      await UsersService.updateUser(
        createProfileSignatureUpdate(profile, graphicalSignature)
      );
      dispatch(setProfileSignature(graphicalSignature));
      return true;
    } catch (err: any) {
      displayErrorNotification(err);
      return false;
    } finally {
      setProcessing(false);
    }
  };

  const handleGraphicalDrawNext = async (): Promise<void> => {
    if (
      processing ||
      !graphicalSignerFullName.trim() ||
      (hasGraphicalSignatureMarks && !graphicalSignature)
    ) return;

    if (!(await persistGraphicalSignature())) return;

    if (hasEditableGraphicalMarks) {
      setSigningFlowStage(SigningFlowStage.GRAPHICAL_MARKS);
      return;
    }

    setSigningFlowStage(SigningFlowStage.GRAPHICAL_RESULTS);
    void handleSubmit();
  };

  const handleGraphicalMarksSubmit = (): void => {
    if (processing || !areRequiredGraphicalMarksFilled) return;

    setSigningFlowStage(SigningFlowStage.GRAPHICAL_RESULTS);
    void handleSubmit();
  };

  const handleGraphicalMarkChange = (
    envelopeKey: string,
    mark: IEnveloperPrepositionedMark,
    value: GraphicalMarkValue
  ): void => {
    setGraphicalMarkValues((currentValues) => {
      const envelopeValues = currentValues[envelopeKey] ?? {};
      const matchingKeys = mark.type === DraftEnvelopePrepositionedMarkType.CUSTOM && mark.label
        ? graphicalEnvelopeFields
          .find(({ envelopeKey: key }) => key === envelopeKey)
          ?.marks
          .filter((candidate) =>
            candidate.type === DraftEnvelopePrepositionedMarkType.CUSTOM &&
            candidate.label === mark.label &&
            isEditableGraphicalMark(candidate)
          )
          .map(({ key }) => key) ?? [mark.key]
        : [mark.key];
      const nextEnvelopeValues = matchingKeys.reduce<Record<string, GraphicalMarkValue>>(
        (result, key) => ({ ...result, [key]: value }),
        envelopeValues
      );

      return {
        ...currentValues,
        [envelopeKey]: nextEnvelopeValues,
      };
    });
  };

  const handleRuCertificateSelect = (certificate: ILocalCertificate): void => {
    setSelectedRuCertificate(certificate);
    setGlobalError('');

    if (ruMandates.length) {
      setSigningFlowStage(SigningFlowStage.RU_POWER);
      return;
    }

    setSigningFlowStage(SigningFlowStage.RU_RESULTS);
    void handleSubmit({ certificate, powerOfAttorneyOguid: null });
  };

  const handleRuPowerSelect = (powerOfAttorneyOguid: string | null): void => {
    if (!selectedRuCertificate) return;

    setSigningFlowStage(SigningFlowStage.RU_RESULTS);
    void handleSubmit({
      certificate: selectedRuCertificate,
      powerOfAttorneyOguid,
    });
  };

  const handleRuSigningBack = (): void => {
    setGlobalError('');
    setSelectedRuCertificate(null);
    setSigningFlowStage(SigningFlowStage.REVIEW);
  };

  const handleVnStepBack = (): void => {
    if (vnSigningStep === VnSigningStep.SIGN) {
      resetSigningOperationState();
    }

    setVnSigningStep(vnSigningStep === VnSigningStep.SIGN ? VnSigningStep.SIGNER_DATA : VnSigningStep.METHOD);
    setGlobalError('');
    setIsCredentialsOpen(false);
  };

  const handleVnStepNext = (): void => {
    setVnSigningStep(vnSigningStep === VnSigningStep.METHOD ? VnSigningStep.SIGNER_DATA : VnSigningStep.SIGN);
    setGlobalError('');
  };

  const handleSavedFptParametersChange = (): void => {
    resetSigningOperationState();
    setIsUsingSavedFptParameters(false);
    setVnSigningStep(VnSigningStep.METHOD);
    setGlobalError('');
  };

  const handleCredentialsOpen = (): void => {
    setGlobalError('');
    setIsCredentialsOpen(true);
  };

  const handleCredentialsClose = (): void => {
    if (processing) return;

    setIsCredentialsOpen(false);
  };

  const handleCredentialsSubmit = async (): Promise<void> => {
    if (!providerUserIdentity || !passcode) return;

    setIsCredentialsOpen(false);
    await handleSubmit();
  };

  const renderHeader = (): ReactNode => (
    <header className={styles.header} id='myModalLabel'>
      <h4 className='m-0'>
        {isFptSignAction
          ? t(vnSigningStep !== VnSigningStep.METHOD
            ? 'envelopes:signAction.method.fpt'
            : 'envelope:digitalSignature')
          : t('envelopes:massActions.title', {
            action: t(getDialogActionTranslation(action)),
            completed: completedCount,
            total: modalEnvelopes.length,
          })}
      </h4>
    </header>
  );

  const renderStatus = (status: MassActionStatus): string => {
    switch (status) {
      case MassActionStatus.PROCESSING:
        return t('envelopes:massActions.status.processing');
      case MassActionStatus.SKIPPED:
        return '';
      default:
        return '';
    }
  };

  const renderRowResult = (row: IEnvelopeMassActionState): ReactNode => {
    if (row.status === MassActionStatus.DONE) {
      return <IconCheck color='success-600' />;
    }

    if (row.status === MassActionStatus.PROCESSING) {
      return (
        <div className={styles.processingResult}>
          <Spinner />
        </div>
      );
    }

    if (row.status === MassActionStatus.WARNING) {
      return (
        <div className={styles.warningResult}>
          <IconWarning color='warning-600' />
          {row.error && <div>{row.error}</div>}
        </div>
      );
    }

    if (row.status === MassActionStatus.ERROR) {
      return (
        <div className={styles.errorResult}>
          <IconWarning color='danger-500' />
          {row.error && <div className='text-danger font-xs'>{row.error}</div>}
        </div>
      );
    }

    return (
      <>
        {renderStatus(row.status)}
        {row.error && <div className='text-danger font-xs'>{row.error}</div>}
      </>
    );
  };

  const renderPreflightError = (error: string): ReactNode => (
    <SystemHint content={error} wrapperClasses='m-auto'>
      <IconProhibited color='danger-500' />
    </SystemHint>
  );

  const renderSignFields = (): ReactNode => {
    if (!isFptSignAction) return null;

    return (
      <div className={styles.signFields}>
        {globalError && !signatureRetry && (
          <Alert
            icon='IconWarning'
            iconProps={{ color: 'warning-600', classes: 'mr-2' }}
            title={globalError}
            theme='danger'
          />
        )}
        <Alert
          icon='IconInfo'
          iconProps={{ color: 'primary-600', classes: 'mr-2' }}
          title={requiresOrganizationSigning
            ? `${t('envelopes:signAction.cloud.organisationRequired')} ${t('envelopes:signAction.cloud.description')}`
            : t('envelopes:signAction.cloud.description')}
          theme='primary'
        />
        <div>
          <div className={styles.fullNameHead}>
            <FormLabel classes='font-xs'>
              {t('envelopes:signAction.cloud.fullName')}
              {isSignerNameRequired && <span className='text-danger'> *</span>}
            </FormLabel>
            {requiresOrganizationSigning && !requiresReusableSigner && (
              <FormLabel className={styles.noNameCheckbox}>
                <Checkbox
                  checked={getCheckboxCheckedValue(isSignerNameOmitted)}
                  className='mr-2'
                  name='isSignerNameOmitted'
                  onChange={handleSignerNameOmittedChange}
                />
                {t('envelopes:signAction.cloud.doNotProvideName')}
              </FormLabel>
            )}
          </div>
          <Input
            name='signerFullName'
            value={signerFullName}
            onChange={(evt) => setSignerFullName(evt.target.value)}
            disabled={isSignerNameOmitted}
          />
        </div>
        {requiresOrganizationSigning && (
          <div className={styles.organisationSection}>
            <div className={styles.organisationSectionHead}>
              <strong>{t('envelopes:signAction.cloud.organisationDetails')}</strong>
              <div className={styles.organisationPicker} ref={organisationPickerRef}>
                <button
                  type='button'
                  className={styles.organisationPickerButton}
                  onClick={handleOrganisationPickerToggle}
                  disabled={isOrganisationSignaturesLoading}
                >
                  {t('envelopes:signAction.cloud.pickOrganisation')}
                </button>
                {isOrganisationPickerOpen && (
                  <div className={styles.organisationPickerPopup}>
                    {organisationSignatures.length ? (
                      organisationSignatures.map((signature) => (
                        <button
                          type='button'
                          className={cn(
                            styles.organisationPickerOption,
                            signature.oguid === organisationSignatureOguid && styles.organisationPickerOptionActive
                          )}
                          key={signature.oguid}
                          onClick={() => handleOrganisationPick(signature)}
                        >
                          <strong>{signature.companyName}</strong>
                          <span>{signature.taxCode}</span>
                        </button>
                      ))
                    ) : (
                      <div className={styles.organisationPickerEmpty}>
                        {t('envelopes:signAction.cloud.noOrganisations')}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
            <div className={styles.organisationCard}>
              {isOrganisationSignaturesLoading ? (
                <div className={styles.organisationCardLoader}>
                  <Spinner />
                </div>
              ) : (
                <>
                  <div className={styles.organisationCardBody}>
                    {isOrganisationEditMode ? (
                      <div className={styles.organisationInfo}>
                        <div className={styles.organisationField}>
                          <FormLabel classes='font-xs'>
                            {t('envelopes:signAction.cloud.companyName')} <span className='text-danger'>*</span>
                          </FormLabel>
                          <Input
                            name='companyName'
                            value={organisationDetails.companyName}
                            placeholder={t('envelopes:signAction.cloud.companyNamePlaceholder')}
                            onChange={handleOrganisationDetailsChange('companyName')}
                          />
                        </div>
                        <div className={styles.organisationInfoRow}>
                          <div className={styles.organisationField}>
                            <FormLabel classes='font-xs'>
                              {t('envelopes:signAction.cloud.taxCode')} <span className='text-danger'>*</span>
                            </FormLabel>
                            <Input
                              name='taxCode'
                              value={organisationDetails.taxCode}
                              placeholder={t('envelopes:signAction.cloud.taxCodePlaceholder')}
                              onChange={handleOrganisationDetailsChange('taxCode')}
                            />
                          </div>
                          <div className={styles.organisationField}>
                            <FormLabel classes='font-xs'>
                              {t('envelopes:signAction.cloud.position')}
                            </FormLabel>
                            <Input
                              name='position'
                              value={organisationDetails.position ?? ''}
                              placeholder={t('envelopes:signAction.cloud.positionPlaceholder')}
                              onChange={handleOrganisationDetailsChange('position')}
                            />
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className={styles.organisationInfoView}>
                        <span className={styles.organisationInfoLabel}>
                          {t('envelopes:signAction.cloud.companyName')}
                        </span>
                        <strong className={styles.organisationInfoValue}>
                          {organisationDetails.companyName || '-'}
                        </strong>
                        <span className={styles.organisationInfoLabel}>
                          {t('envelopes:signAction.cloud.taxCode')}
                        </span>
                        <strong className={styles.organisationInfoValue}>
                          {organisationDetails.taxCode || '-'}
                        </strong>
                        <span className={styles.organisationInfoLabel}>
                          {t('envelopes:signAction.cloud.position')}
                        </span>
                        <strong className={styles.organisationInfoValue}>
                          {organisationDetails.position || '-'}
                        </strong>
                      </div>
                    )}
                    <div className={styles.organisationStamp}>
                      <FormLabel classes='font-xs'>
                        {t('envelopes:signAction.cloud.organisationStamp')}
                        {isOrganisationEditMode && <span className='text-danger'> *</span>}
                      </FormLabel>
                      {isOrganisationEditMode ? (
                        <>
                          <label
                            className={styles.organisationStampUpload}
                            onDragOver={handleOrganisationStampDragOver}
                            onDrop={handleOrganisationStampDrop}
                          >
                            {organisationDetails.stampBase64 ? (
                              <span className={styles.organisationStampPreview}>
                                <img
                                  src={getImageBase64Src(organisationDetails.stampBase64)}
                                  alt={organisationDetails.companyName}
                                />
                              </span>
                            ) : (
                              <span className={styles.organisationStampDropzone}>
                                <IconCloud color='primary-600' size='lg' />
                                <strong>{t('envelopes:signAction.cloud.uploadStamp')}</strong>
                                <small>{t('envelopes:signAction.cloud.uploadStampHint')}</small>
                              </span>
                            )}
                            <input
                              type='file'
                              accept='image/png,image/jpeg'
                              className='d-none'
                              onChange={handleOrganisationStampChange}
                            />
                          </label>
                          <button
                            type='button'
                            className={styles.removeBackgroundButton}
                            onClick={handleOrganisationStampRemoveBackground}
                          >
                            {t('envelopes:signAction.cloud.removeBackground')}
                          </button>
                        </>
                      ) : organisationDetails.stampBase64 ? (
                        <img
                          src={getImageBase64Src(organisationDetails.stampBase64)}
                          alt={organisationDetails.companyName}
                        />
                      ) : (
                        <div className={styles.organisationStampEmpty} />
                      )}
                    </div>
                  </div>
                  {!isOrganisationEditMode && (
                    <div className={styles.organisationCardActions}>
                      <Button theme='link' linkStyle='none' onClick={handleOrganisationEdit}>
                        <IconEdit classes='mr-2' />
                        {t('common:edit')}
                      </Button>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderEnvelopeAction = (envelope: IEnvelope): ReactNode => {
    if (!isEnvelopeActionAllowed(envelope)) {
      return (
        <span className={styles.unavailable}>
          {t('envelopes:massActions.actionUnavailable')}
        </span>
      );
    }

    return t(getDialogActionTranslation(action));
  };

  const renderSigningTypeSelector = (): ReactNode => {
    if (
      !isMassSignAction ||
      checkingSignData ||
      !selectedSigningType ||
      availableSigningTypes.length < 2 ||
      signingFlowStage !== SigningFlowStage.REVIEW
    ) return null;

    return (
      <div className={styles.signingTypeSelector}>
        {[
          {
            type: MassSigningType.GRAPHICAL,
            label: t('profile:fieldsWrapperTitle.graphicSignature'),
          },
          {
            type: MassSigningType.ELECTRONIC,
            label: t('envelope:digitalSignature'),
          },
        ].map(({ type, label }) => (
          <FormLabel className={styles.signingTypeOption} key={type}>
            <RadioButton
              checked={selectedSigningType === type}
              className='mr-2'
              disabled={!availableSigningTypes.includes(type) || processing}
              name='mass_signing_type'
              onChange={() => handleSigningTypeSelect(type)}
              value={type}
            />
            {label}
          </FormLabel>
        ))}
      </div>
    );
  };

  const renderBody = (withSignFields = true): ReactNode => (
    <div className={styles.body}>
      {renderSigningTypeSelector()}
      {globalError && withSignFields && <div className={styles.error}>{globalError}</div>}
      {isFptSignAction && !withSignFields && processed ? (
        <Alert
          icon='IconInfo'
          iconProps={{ color: 'primary-600', classes: 'mr-2' }}
          title={t('envelopes:signAction.resultMessage', {
            completedCount,
            allowedCount,
          })}
          theme='primary'
        />
      ) : allowedCount < modalEnvelopes.length ? (
        <div className={styles.notice}>
          {t('envelopes:massActions.availableCount', {
            allowed: allowedCount,
            total: modalEnvelopes.length,
          })}
        </div>
      ) : null}
      {withSignFields && renderSignFields()}
      {isDeclineAction && (
        <div className={styles.commentMobile}>
          <FormLabel htmlFor='mass_action_comment'>{t('common:comment')}</FormLabel>
          <Input
            name='mass_action_comment'
            value={comment}
            onChange={handleCommentChange}
            placeholder={t('envelopes:massActions.commentOptional')}
          />
        </div>
      )}
      <div className='mt-3 relative'>
        <Table
          fixedType={LibFixedType.MODAL}
          fixedModalHeight='260px'
          isUpdateTableY={!isMassSignAction}
          headings={[
            { content: t('envelopes:massActions.envelope') },
            { content: t('envelopes:massActions.action'), class: 'text-center' },
            { content: t('envelopes:massActions.result'), class: 'text-center' },
          ]}
        >
          <>
            {modalEnvelopes.map((envelope, index) => {
              const key = rowKeys[index];
              const row = rows[key] ?? { status: MassActionStatus.QUEUED };
              const isAllowed = isEnvelopeActionAllowed(envelope);
              const preflightError = signingPreflightErrors[key];
              return (
                <tr key={key}>
                  <td className={styles.td_envelopeSubject}>
                    <div className={cn(styles.envelopeSubject, styles.td_envelopeSubject, !isAllowed && styles.unavailable)}>
                      {envelope.subject}
                    </div>
                  </td>
                  <td className={cn(styles.td_action, 'text-center')}>{renderEnvelopeAction(envelope)}</td>
                  <td className={styles.td_result}>
                    <div className={cn(
                      styles.result,
                      'text-center',
                      preflightError && styles.errorText,
                      row.status === MassActionStatus.ERROR && styles.errorText,
                      row.status === MassActionStatus.WARNING && styles.warningText
                    )}>
                      {preflightError
                        ? renderPreflightError(preflightError)
                        : renderRowResult(row)}
                    </div>
                  </td>
                </tr>
              );
            })}
          </>
        </Table>
      </div>
    </div>
  );

  const renderVnStepNav = (): ReactNode => (
    <div className={styles.steps}>
      {[
        {
          step: VnSigningStep.METHOD,
          title: t('envelopes:signAction.step.chooseMethod'),
        },
        {
          step: VnSigningStep.SIGNER_DATA,
          title: t('envelopes:signAction.step.signerData'),
        },
        {
          step: VnSigningStep.SIGN,
          title: t('envelopes:signAction.step.sign'),
        },
      ].map(({ step, title }, index, list) => {
        const currentIndex = list.findIndex(({ step: itemStep }) => itemStep === vnSigningStep);
        const isActive = vnSigningStep === step;
        const isPassed = currentIndex > index;

        return (
          <div
            className={cn(styles.step, {
              [styles.stepActive]: isActive,
              [styles.stepPassed]: isPassed,
            })}
            key={step}
          >
            <span className={cn(
              'align-items-center d-flex justify-content-center relative text-light mr-2 fw-600',
              styles.stepNum
            )}>
              {isPassed ? <IconCheck color='light' size='sm' /> : index + 1}
            </span>
            <span className={styles.stepLabel}>
              {title}
            </span>
          </div>
        );
      })}
    </div>
  );

  const renderVnMethodStep = (): ReactNode => (
    <div className={styles.vnBody}>
      {renderVnStepNav()}
      <div className={styles.vnStepContent}>
        {signDataError && (
          <Alert
            icon='IconWarning'
            iconProps={{ color: 'warning-600', classes: 'mr-2' }}
            title={signDataError}
            theme='danger'
          />
        )}
        <button
          type='button'
          className={cn(styles.methodCard, styles.methodCardActive)}
          onClick={handleVnStepNext}
          disabled={checkingSignData || Boolean(signDataError)}
        >
          <span className={styles.methodRadio} />
          <span className={styles.methodLogo}>FPT</span>
          <span>
            <strong>{t('envelopes:signAction.method.fpt')}</strong>
            <small>{t('envelopes:signAction.method.fptDescription')}</small>
          </span>
        </button>
      </div>
    </div>
  );

  const renderVnSignerDataStep = (): ReactNode => (
    <div className={styles.vnBody}>
      {renderVnStepNav()}
      <div className={styles.vnStepContent}>
        {signDataError && (
          <Alert
            icon='IconWarning'
            iconProps={{ color: 'warning-600', classes: 'mr-2' }}
            title={signDataError}
            theme='danger'
          />
        )}
        {renderSignFields()}
      </div>
    </div>
  );

  const renderSavedFptParameters = (): ReactNode => (
    <div className={styles.savedParameters}>
      <div className={styles.savedParametersHead}>
        <div className={styles.savedMethod}>
          <span className={styles.methodLogo}>FPT</span>
          <span>
            <strong>{t('envelopes:signAction.method.fpt')}</strong>
            <small>{t('envelopes:signAction.method.fptDescription')}</small>
          </span>
        </div>
        <button
          type='button'
          className={styles.savedParametersChange}
          onClick={handleSavedFptParametersChange}
          disabled={processing}
        >
          {t('common:change')}
        </button>
      </div>
      <div className={styles.savedSigner}>
        <span>{t('envelopes:signAction.cloud.fullName')}</span>
        <strong>
          {isSignerNameOmitted
            ? t('envelopes:signAction.cloud.doNotProvideName')
            : signerFullName || profile.fullName || '-'}
        </strong>
      </div>
      {requiresOrganizationSigning && (
        <div className={styles.savedOrganisation}>
          <strong>{t('envelopes:signAction.cloud.organisationDetails')}</strong>
          <div className={styles.savedOrganisationDetails}>
            <span>{t('envelopes:signAction.cloud.companyName')}</span>
            <strong>{organisationDetails.companyName || '-'}</strong>
            <span>{t('envelopes:signAction.cloud.taxCode')}</span>
            <strong>{organisationDetails.taxCode || '-'}</strong>
            <span>{t('envelopes:signAction.cloud.position')}</span>
            <strong>{organisationDetails.position || '-'}</strong>
          </div>
        </div>
      )}
    </div>
  );

  const renderVnSignStep = (): ReactNode => (
    <div className={styles.vnBody}>
      {!isUsingSavedFptParameters && renderVnStepNav()}
      <div className={styles.vnStepContent}>
        {isUsingSavedFptParameters && renderSavedFptParameters()}
        {signatureRetry && (
          <Alert
            icon='IconWarning'
            iconProps={{ color: 'warning-600', classes: 'mr-2' }}
            title={t('envelopes:signAction.cloud.ignoreAndSignWarning')}
            theme='warning'
          />
        )}
        {renderBody(false)}
      </div>
    </div>
  );

  const renderVnLoading = (): ReactNode => (
    <div className={styles.vnBody}>
      <div className={styles.savedParametersLoader}>
        <Spinner />
      </div>
    </div>
  );

  const renderVnBody = (): ReactNode => {
    if (isSavedFptParametersLoading) return renderVnLoading();

    return vnSigningStep === VnSigningStep.METHOD
      ? renderVnMethodStep()
      : vnSigningStep === VnSigningStep.SIGNER_DATA
        ? renderVnSignerDataStep()
        : renderVnSignStep();
  };

  const renderRuCertificateRow = (certificate: Certificate): ReactNode => {
    const { dateFrom, dateTo, info, key } = getCertificateData(
      certificate,
      ruCryptoRef.current,
      t
    );
    const isExpired = Boolean(
      dateTo && DateTime.fromISO(dateTo).toMillis() < Date.now()
    );

    if (!('validToDate' in certificate)) return null;

    return (
      <tr
        className={cn('pointer', isExpired && 'text-danger')}
        key={key}
        onClick={() => handleRuCertificateSelect(certificate)}
      >
        <td>{info ?? t('common:notSpecifiedAlt')}</td>
        <td>
          {getCertificateValidity(dateFrom, dateTo, ruCryptoRef.current, t)}
        </td>
        <td>{ruMandates.length ? `${ruMandates.length} ${t('profile:PoA.units')}` : ''}</td>
      </tr>
    );
  };

  const renderRuCertificateStep = (): ReactNode => (
    <>
      {renderHeader()}
      <div className={styles.body}>
        {globalError && (
          <Alert
            icon='IconWarning'
            iconProps={{ color: 'warning-600', classes: 'mr-2' }}
            title={globalError}
            theme='danger'
          />
        )}
        {!isRuPluginAvailable && (
          <div className={styles.error}>
            {t('envelopes:signAction.unavailable')}
          </div>
        )}
        <Button
          onClick={() => setIsRuCertificateSettingsOpen(true)}
          isDisabled={processing}
        >
          {t('envelopes:signAction.certificate.settings')}
        </Button>
        <div className='mt-3 relative'>
          <Table
            fixedType={LibFixedType.MODAL}
            fixedModalHeight='260px'
            headings={[
              { content: t('envelopes:signAction.certificate.table.owner') },
              { content: t('envelopes:signAction.certificate.table.validity') },
              { content: t('envelopes:signAction.certificate.PoA.title') },
            ]}
          >
            <>{ruCertificates.map(renderRuCertificateRow)}</>
          </Table>
        </div>
      </div>
      <footer className={styles.footer}>
        <Button onClick={handleRuSigningBack} isDisabled={processing}>
          {t('common:back')}
        </Button>
        <div />
        <Button onClick={handleCloseClick} isDisabled={processing}>
          {t('common:cancel')}
        </Button>
      </footer>
    </>
  );

  const renderRuResults = (): ReactNode => (
    <>
      {renderHeader()}
      {renderBody(false)}
      <footer className={styles.footer}>
        <div />
        <div />
        <Button onClick={handleCloseClick} isDisabled={processing}>
          {t('common:close')}
        </Button>
      </footer>
    </>
  );

  const isBaseSubmitDisabled =
    processing ||
    processed ||
    !modalEnvelopes.some(isEnvelopeActionAllowed) ||
    Boolean(signDataError);


  const isSignerDataNextDisabled =
    processing ||
    checkingSignData ||
    Boolean(signDataError) ||
    (isSignerNameRequired && !signerFullName) ||
    (isOrganisationSignaturesLoading && !isUsingSavedFptParameters) ||
    isOrganisationDetailsIncomplete;

  const isCredentialsSubmitDisabled =
    processing ||
    isBaseSubmitDisabled ||
    isSignerDataNextDisabled ||
    checkingSignData ||
    !providerUserIdentity ||
    !passcode;

  const isSubmitDisabled = isBaseSubmitDisabled ||
    (isMassSignAction && checkingSignData) ||
    (isFptSignAction && (
      !providerUserIdentity ||
      !passcode ||
      isOrganisationSignaturesLoading ||
      isOrganisationDetailsIncomplete
    )
  );
  const isVnBackVisible =
    vnSigningStep !== VnSigningStep.METHOD &&
    !isUsingSavedFptParameters &&
    !isSavedFptParametersLoading;

  const renderFooter = (): ReactNode => (
    <footer className={cn(styles.massActionFooter, {
      [styles.massActionFooterWithComment]: isDeclineAction,
    })}>
      {isDeclineAction ? (
        <div className={styles.comment}>
          <Input
            name='mass_action_comment_footer'
            value={comment}
            onChange={handleCommentChange}
            placeholder={t('envelopes:massActions.commentOptional')}
          />
        </div>
      ) : (
        <div className={styles.comment} />
      )}
      <div className={styles.massActionFooterActions}>
        {isElectronicSigningSelected && signDataError && (
          <div className={styles.footerError} role='alert'>
            {signDataError}
          </div>
        )}
        <Button
          theme='primary'
          onClick={isMassSignAction && signingFlowStage === SigningFlowStage.REVIEW
            ? handleSigningReviewSubmit
            : () => void handleSubmit()}
          isDisabled={isSubmitDisabled}
        >
          {signatureRetry
            ? t('envelopes:signAction.cloud.ignoreAndSign')
            : t(getDialogActionTranslation(action))}
        </Button>
        <Button onClick={handleCloseClick} externalClass='ml-4' isDisabled={processing}>
          {t('common:close')}
        </Button>
      </div>
    </footer>
  );

  const renderVnFooter = (): ReactNode => (
    <footer className={styles.vnFooter}>
      {isVnBackVisible && (
        <Button onClick={handleVnStepBack} isDisabled={processing}>
          {t('common:back')}
        </Button>
      )}
      {!isVnBackVisible && <div />}
      <div className={styles.vnFooterActions}>
        {isSavedFptParametersLoading ? null : vnSigningStep !== VnSigningStep.SIGN ? (
          <Button
            theme='primary'
            onClick={handleVnStepNext}
            isDisabled={vnSigningStep === VnSigningStep.METHOD
              ? checkingSignData || Boolean(signDataError)
              : isSignerDataNextDisabled}
          >
            {t('common:next')}
          </Button>
        ) : (
          <Button
            theme='primary'
            onClick={handleCredentialsOpen}
            isDisabled={
              isBaseSubmitDisabled ||
              isSignerDataNextDisabled ||
              checkingSignData
            }
          >
            {signatureRetry
              ? t('envelopes:signAction.cloud.ignoreAndSign')
              : t('envelopes:signAction.cloud.sign')}
          </Button>
        )}
        <Button onClick={handleCloseClick} externalClass='ml-4' isDisabled={processing}>
          {t('common:cancel')}
        </Button>
      </div>
    </footer>
  );

  const renderCredentialsDialog = (): ReactNode => {
    if (!isCredentialsOpen) return null;

    return (
      <div className={styles.credentialsOverlay}>
        <div className={styles.credentialsDialog}>
          <h4>{t('envelopes:signAction.cloud.credentialsTitle')}</h4>
          <div className={styles.credentialsForm}>
            <div>
              <FormLabel classes='font-xs'>{t('envelopes:signAction.cloud.providerUserIdentity')}</FormLabel>
              <Input
                {...FPT_PROVIDER_USER_IDENTITY_INPUT_PROPS}
                value={providerUserIdentity}
                onChange={(evt) => setProviderUserIdentity(evt.target.value)}
              />
            </div>
            <div>
              <FormLabel classes='font-xs'>{t('envelopes:signAction.cloud.passcode')}</FormLabel>
              <Input
                {...FPT_PASSCODE_INPUT_PROPS}
                value={passcode}
                onChange={(evt) => setPasscode(evt.target.value)}
              />
            </div>
          </div>
          <div className={styles.credentialsActions}>
            <Button onClick={handleCredentialsClose} isDisabled={processing}>
              {t('common:cancel')}
            </Button>
            <Button
              theme='primary'
              onClick={handleCredentialsSubmit}
              isDisabled={isCredentialsSubmitDisabled}
            >
              {signatureRetry
                ? t('envelopes:signAction.cloud.ignoreAndSign')
                : t('envelopes:signAction.cloud.sign')}
            </Button>
          </div>
        </div>
      </div>
    );
  };

  const renderVnModalContent = (): ReactNode => (
    <div className={styles.vnModal}>
      {renderHeader()}
      {renderVnBody()}
      {renderVnFooter()}
      {renderCredentialsDialog()}
    </div>
  );

  const renderGraphicalFooter = (
    onBack: () => void,
    onNext: () => void,
    isNextDisabled: boolean,
    nextLabel = t('common:next')
  ): ReactNode => (
    <footer className={styles.massActionFooter}>
      <Button onClick={onBack} isDisabled={processing}>
        {t('common:back')}
      </Button>
      <div className={styles.massActionFooterActions}>
        <Button theme='primary' onClick={onNext} isDisabled={isNextDisabled}>
          {nextLabel}
        </Button>
        <Button onClick={handleCloseClick} externalClass='ml-4' isDisabled={processing}>
          {t('common:cancel')}
        </Button>
      </div>
    </footer>
  );

  const renderGraphicalDrawStep = (): ReactNode => (
    <div className={styles.massActionModal}>
      {renderHeader()}
      <div className={styles.massActionBody}>
        <div className={styles.massActionContent}>
          <GraphicalSignatureForm
            disabled={processing}
            fullName={graphicalSignerFullName}
            onFullNameChange={setGraphicalSignerFullName}
            onSignatureChange={handleGraphicalSignatureChange}
            profileSignature={profile.electronicSignatureImage}
            signature={graphicalSignature}
            signatureFull={graphicalSignatureFull}
            signatureSource={graphicalSignatureSource}
          />
        </div>
      </div>
      {renderGraphicalFooter(
        () => setSigningFlowStage(SigningFlowStage.REVIEW),
        handleGraphicalDrawNext,
        processing ||
          !graphicalSignerFullName.trim() ||
          (hasGraphicalSignatureMarks && !graphicalSignature)
      )}
    </div>
  );

  const getGraphicalMarkLabel = (mark: IEnveloperPrepositionedMark): string =>
    mark.label || t(`envelope:markType.${mark.type.toLowerCase()}`);

  const renderGraphicalMarkInput = (
    envelopeKey: string,
    mark: IEnveloperPrepositionedMark
  ): ReactNode => {
    const inputId = `mass_graphical_mark_${envelopeKey}_${mark.key}`;
    const value = graphicalMarkValues[envelopeKey]?.[mark.key] ??
      getInitialGraphicalMarkValue(mark);
    const label = getGraphicalMarkLabel(mark);

    if (mark.type === DraftEnvelopePrepositionedMarkType.CHECKBOX) {
      return (
        <FormLabel className={styles.graphicalCheckboxLabel} htmlFor={inputId}>
          <Checkbox
            checked={getCheckboxCheckedValue(Boolean(value))}
            className='mr-2'
            disabled={processing}
            id={inputId}
            name={inputId}
            onChange={() => handleGraphicalMarkChange(envelopeKey, mark, !value)}
          />
          <span>
            {label}
            {mark.isRequired && <span className='text-danger'> *</span>}
          </span>
        </FormLabel>
      );
    }

    if (mark.type === DraftEnvelopePrepositionedMarkType.SELECT) {
      const options: ILibOption[] = (mark.valueList ?? []).map((option) => ({
        label: option,
        value: option,
      }));

      return (
        <div>
          <FormLabel classes='font-xs' htmlFor={inputId}>
            {label}{mark.isRequired && <span className='text-danger'> *</span>}
          </FormLabel>
          <SelectWrapper
            id={inputId}
            isDisabled={processing}
            name={inputId}
            onChange={(option) => {
              if (option) handleGraphicalMarkChange(envelopeKey, mark, option.value);
            }}
            options={options}
            rawValue={typeof value === 'string' ? value : ''}
          />
          {mark.tooltip && <small className={styles.graphicalMarkHint}>{mark.tooltip}</small>}
        </div>
      );
    }

    return (
      <div>
        <FormLabel classes='font-xs' htmlFor={inputId}>
          {label}{mark.isRequired && <span className='text-danger'> *</span>}
        </FormLabel>
        {mark.type === DraftEnvelopePrepositionedMarkType.TEXT ? (
          <textarea
            className={styles.graphicalMarkTextarea}
            disabled={processing}
            id={inputId}
            maxLength={2000}
            name={inputId}
            onChange={(evt) => handleGraphicalMarkChange(envelopeKey, mark, evt.target.value)}
            value={typeof value === 'string' ? value : ''}
          />
        ) : (
          <Input
            disabled={processing}
            id={inputId}
            maxLength={2000}
            name={inputId}
            onChange={(evt) => handleGraphicalMarkChange(envelopeKey, mark, evt.target.value)}
            value={typeof value === 'string' ? value : ''}
          />
        )}
        {mark.tooltip && <small className={styles.graphicalMarkHint}>{mark.tooltip}</small>}
      </div>
    );
  };

  const renderGraphicalMarksStep = (): ReactNode => (
    <div className={styles.massActionModal}>
      {renderHeader()}
      <div className={styles.massActionBody}>
        <div className={styles.massActionContent}>
          <div className={styles.graphicalMarks}>
            {graphicalEnvelopeFields.map(({ envelope, envelopeKey, marks }) => {
              const handledCustomLabels = new Set<string>();
              const editableMarks = marks.filter(isEditableGraphicalMark).filter((mark) => {
                if (mark.type !== DraftEnvelopePrepositionedMarkType.CUSTOM || !mark.label) {
                  return true;
                }
                if (handledCustomLabels.has(mark.label)) return false;

                handledCustomLabels.add(mark.label);
                return true;
              });

              if (!editableMarks.length) return null;

              return (
                <section className={styles.graphicalEnvelopeMarks} key={envelopeKey}>
                  <h5>{envelope.subject}</h5>
                  <div className={styles.graphicalMarkFields}>
                    {editableMarks.map((mark) => (
                      <div className={styles.graphicalMarkField} key={mark.key}>
                        {renderGraphicalMarkInput(envelopeKey, mark)}
                      </div>
                    ))}
                  </div>
                </section>
              );
            })}
          </div>
        </div>
      </div>
      {renderGraphicalFooter(
        () => setSigningFlowStage(SigningFlowStage.GRAPHICAL_DRAW),
        handleGraphicalMarksSubmit,
        processing || !areRequiredGraphicalMarksFilled,
        t(getDialogActionTranslation(action))
      )}
    </div>
  );

  const renderGraphicalResults = (): ReactNode => (
    <div className={styles.massActionModal}>
      {renderHeader()}
      <div className={styles.massActionBody}>
        <div className={styles.massActionContent}>
          {renderBody(false)}
        </div>
      </div>
      <footer className={styles.massActionFooter}>
        <div />
        <div className={styles.massActionFooterActions}>
          <Button onClick={handleCloseClick} isDisabled={processing}>
            {t('common:close')}
          </Button>
        </div>
      </footer>
    </div>
  );

  const renderInitialModalContent = (loading = false): ReactNode => (
    <div className={styles.massActionModal}>
      {renderHeader()}
      {loading ? renderVnLoading() : (
        <div className={styles.massActionBody}>
          <div className={styles.massActionContent}>
            {renderBody()}
          </div>
        </div>
      )}
      {renderFooter()}
    </div>
  );

  const renderRuPowerStep = (): ReactNode => (
    <CertificatePower
      boxOguid={currentMassBoxOguid}
      inProgress={processing}
      isEdit={false}
      onClose={() => setSigningFlowStage(SigningFlowStage.RU_CERTIFICATE)}
      onPowerOfAttorneySelect={handleRuPowerSelect}
      setInProgress={setProcessing}
    />
  );

  return (
    <ModalWrapper
      id={Modal.ENVELOPE_MASS_ACTION}
      size={ILibModalSizes.MEDIUM}
      onCloseModal={handleClose}
      backdrop={false}
      disableHideFn={processing}
      portalClasses='envelope-mass-action-portal'
    >
      {isSigningDetailsLoading ? (
        renderInitialModalContent(true)
      ) : isRuCertificateSettingsOpen ? (
        <CertificateSettings
          isAccessPlugin={isRuPluginAvailable}
          onAccessPluginStatusChange={setIsRuPluginAvailable}
          onCloseModal={() => setIsRuCertificateSettingsOpen(false)}
        />
      ) : isRuElectronicSignAction && signingFlowStage === SigningFlowStage.RU_POWER ? (
        renderRuPowerStep()
      ) : isRuElectronicSignAction && signingFlowStage === SigningFlowStage.RU_CERTIFICATE ? (
        renderRuCertificateStep()
      ) : isRuElectronicSignAction && signingFlowStage === SigningFlowStage.RU_RESULTS ? (
        renderRuResults()
      ) : isGraphicalSigningSelected &&
        signingFlowStage === SigningFlowStage.GRAPHICAL_DRAW ? (
          renderGraphicalDrawStep()
      ) : isGraphicalSigningSelected &&
        signingFlowStage === SigningFlowStage.GRAPHICAL_MARKS ? (
          renderGraphicalMarksStep()
      ) : isGraphicalSigningSelected &&
        signingFlowStage === SigningFlowStage.GRAPHICAL_RESULTS ? (
          renderGraphicalResults()
      ) : isFptSignAction ? (
        renderVnModalContent()
      ) : (
        renderInitialModalContent()
      )}
    </ModalWrapper>
  );
};
